import { FC, useCallback, useEffect, useState } from 'react';

import { ExclamationCircleOutlined } from '@ant-design/icons';
import { Button, Col, Row, Select, Spin, Switch, Tabs, Tooltip } from 'antd';
import { Modal as antModal } from 'antd';
import { useForm, useWatch } from 'antd/es/form/Form';
import { useParams } from 'react-router-dom';
import { MY_EVENTS_ROUTE } from 'routes/events/list';
import { Form, Icon, Input, Label, Link, Modal, TextArea, Typography, message } from 'ui';

import { activeEventNameSelector } from 'selectors/eventsSlice.selector';
import {
  useCreateExtraFieldGroupingMutation,
  useCreateExtraFieldMutation,
  useDeleteExtraFieldGroupingsMutation,
  useDeleteExtraFieldMutation,
  useEditExtraFieldGroupingMutation,
  useEditExtraFieldMutation,
  useLazyGetExtraFieldGroupingsQuery,
} from 'services/events/eventsApiService';
import {
  ExtraField,
  ExtraFieldGrouping,
  ExtraFieldGroupingResType,
} from 'services/events/evetns.api.type';
import { useAppDispatch, useAppSelector } from 'store/index';

import EventLoadingState from 'containers/events-loading-state';

import s from './ExtraFields.module.scss';
import ExtraFieldGroupingItem from './extra-field-grouping-item';
import ExtraFieldsItem from './extra-field-item';

const { confirm } = antModal;

const Notifications: FC = () => {
  const { eventId } = useParams();
  const dispatch = useAppDispatch();
  const [form] = useForm();
  const [fieldForm] = useForm();

  const eventName = useAppSelector(activeEventNameSelector);
  const fieldType = useWatch('fieldType', fieldForm);
  const [createExtraFieldGrouping, { isLoading: creating }] = useCreateExtraFieldGroupingMutation();
  const [deleteExtraFieldGroupings] = useDeleteExtraFieldGroupingsMutation();
  const [editExtraFieldGrouping, { isLoading: editing }] = useEditExtraFieldGroupingMutation();
  const [getExtraFieldGroupings, { isFetching: fetching }] = useLazyGetExtraFieldGroupingsQuery();

  const [createExtraField, { isLoading: creatingExtraField }] = useCreateExtraFieldMutation();
  const [deleteExtraField] = useDeleteExtraFieldMutation();
  const [editExtraField, { isLoading: editingExtraField }] = useEditExtraFieldMutation();

  const [tabKey, setTabKey] = useState<string>('group');

  const [editGrouping, setEditGrouping] = useState<ExtraFieldGrouping | null>(null);
  const [open, setOpen] = useState(false);
  const [extraFieldGroupingData, setExtraFieldGroupingData] = useState<ExtraFieldGroupingResType>();

  const [editField, setEditField] = useState<ExtraField | null>(null);
  const [openField, setOpenField] = useState(false);

  const showModal = () => {
    setEditGrouping(null);
    setOpen(true);
  };

  const showFieldModal = () => {
    setEditField(null);
    setOpenField(true);
  };

  const handleOk = () => {
    setOpen(false);
  };

  const handleFieldOk = () => {
    setOpenField(false);
  };

  const handleCancel = () => {
    form.resetFields();
    setOpen(false);
  };

  const handleFieldCancel = () => {
    fieldForm.resetFields();
    setOpenField(false);
  };

  useEffect(() => {
    const fetchNotifications = async () => {
      if (eventId) {
        try {
          const res = await getExtraFieldGroupings(eventId);
          if ('data' in res) {
            setExtraFieldGroupingData(res?.data);
          }
        } catch (error) {
          console.error('Error fetching data:', error);
        }
      }
    };

    fetchNotifications();
  }, [getExtraFieldGroupings, eventId, dispatch]);

  const handleTabChange = (key) => {
    setTabKey(key);
  };

  const showConfirm = async (id: string) => {
    confirm({
      title: 'Do you want to delete this grouping?',
      icon: <ExclamationCircleOutlined />,
      content: 'This action cannot be undone.',
      centered: true,
      mask: false,
      async onOk() {
        const res = await deleteExtraFieldGroupings(id);

        if ('data' in res) {
          setExtraFieldGroupingData(res?.data);
          message.success('Grouping removed successfully.');
        }
      },
      onCancel() {
        console.log('Cancel');
      },
    });
  };

  const showFieldDeleteConfirm = async (id: string) => {
    confirm({
      title: 'Do you want to delete this extra field?',
      icon: <ExclamationCircleOutlined />,
      content: 'This action cannot be undone.',
      centered: true,
      mask: false,
      async onOk() {
        const res = await deleteExtraField(id);

        if ('data' in res) {
          setExtraFieldGroupingData(res?.data);
          message.success('Extra field removed successfully.');
        }
      },
      onCancel() {
        console.log('Cancel');
      },
    });
  };

  const handleDeleteItem = (id: string) => {
    showConfirm(id);
  };

  const handleDeleteFieldItem = (id: string) => {
    showFieldDeleteConfirm(id);
  };

  const handleEditItem = (grouping: ExtraFieldGrouping) => {
    setEditGrouping(grouping);
    setOpen(true);
  };

  const handleEditFieldItem = (field: ExtraField) => {
    setEditField(field);
    setOpenField(true);
  };

  useEffect(() => {
    if (form && editGrouping && open) {
      form.setFieldValue('name', editGrouping?.name);
      form.setFieldValue('showTitle', editGrouping?.showTitle);
      form.setFieldValue('captureType', editGrouping?.captureType);
      form.setFieldValue('sortOrder', editGrouping?.sortOrder);
    }
  }, [editGrouping, open, form]);

  useEffect(() => {
    if (fieldForm && editField && openField) {
      fieldForm.setFieldValue('groupId', editField?.groupId);
      fieldForm.setFieldValue('fieldType', editField?.fieldType);
      fieldForm.setFieldValue('caption', editField?.caption);
      fieldForm.setFieldValue('optionsAvailable', editField?.optionsAvailable);
      fieldForm.setFieldValue('name', editField?.name);
      fieldForm.setFieldValue('captionToShowOnTicket', editField?.captionToShowOnTicket);
      fieldForm.setFieldValue('sortOrder', editField?.sortOrder);
      fieldForm.setFieldValue('defaultValue', editField?.defaultValue);
      fieldForm.setFieldValue('helpInformation', editField?.helpInformation);
    }
  }, [editField, openField, fieldForm]);

  const handleSubmit = useCallback(
    async (data) => {
      if (data && eventId) {
        if (!editGrouping) {
          const result = {
            eventId: eventId,
            name: data.name,
            showTitle: data.showTitle,
            sortOrder: data.sortOrder,
            captureType: data.captureType,
          };
          const res = await createExtraFieldGrouping(result);
          if ('data' in res) {
            setOpen(false);
            form.resetFields();
            setExtraFieldGroupingData(res?.data);
            message.success('Successfully created grouping.');
          }
        } else {
          const result = {
            id: editGrouping.id,
            eventId: eventId,
            name: data.name,
            showTitle: data.showTitle,
            sortOrder: data.sortOrder,
            captureType: data.captureType,
          };
          const res = await editExtraFieldGrouping(result);
          if ('data' in res) {
            setOpen(false);
            form.resetFields();
            setExtraFieldGroupingData(res?.data);
            message.success('Successfully updated grouping.');
          }
        }
      }
    },
    [createExtraFieldGrouping, form, eventId, editExtraFieldGrouping, editGrouping],
  );

  const handleFieldSubmit = useCallback(
    async (data) => {
      if (data) {
        if (!editField) {
          const result = {
            groupId: data.groupId,
            fieldType: data.fieldType,
            caption: data.caption,
            optionsAvailable: data.optionsAvailable,
            name: data.name,
            sortOrder: data.sortOrder,
            captionToShowOnTicket: data.captionToShowOnTicket,
            defaultValue: data.defaultValue,
            helpInformation: data.helpInformation,
          };
          const res = await createExtraField(result);
          if ('data' in res) {
            setOpenField(false);
            fieldForm.resetFields();
            setExtraFieldGroupingData(res?.data);
            message.success('Successfully created extra field.');
          }
        } else {
          const result = {
            id: editField.id,
            groupId: data.groupId,
            fieldType: data.fieldType,
            caption: data.caption,
            optionsAvailable: data.optionsAvailable,
            name: data.name,
            sortOrder: data.sortOrder,
            captionToShowOnTicket: data.captionToShowOnTicket,
            defaultValue: data.defaultValue,
            helpInformation: data.helpInformation,
          };
          const res = await editExtraField(result);
          if ('data' in res) {
            setOpenField(false);
            fieldForm.resetFields();
            setExtraFieldGroupingData(res?.data);
            message.success('Successfully updated extra field.');
          }
        }
      }
    },
    [createExtraField, fieldForm, editExtraField, editField],
  );

  return (
    <div className={s.wrapper}>
      <Link
        to={MY_EVENTS_ROUTE}
        icon="arrowLeft"
        label="My Events"
        labelType="h6"
        className={s.backLink}></Link>
      <div className={s.row}>
        <div className={s.titileHeader}>
          {eventName && (
            <Typography type="h2" weight={700}>
              &quot;{eventName ?? ''}&quot;&nbsp;Extra fields
            </Typography>
          )}
        </div>
        <div className={s.titleButton}>
          {tabKey === 'group' ? (
            <Button
              type="primary"
              size="large"
              onClick={showModal}
              style={{ fontSize: 13, paddingLeft: 40, paddingRight: 40 }}>
              + CREATE GROUPING
            </Button>
          ) : (
            <Tooltip
              title={
                !extraFieldGroupingData?.groupings?.length
                  ? 'Create a grouping first before adding extra fields.'
                  : ''
              }>
              <span>
                <Button
                  type="primary"
                  size="large"
                  onClick={showFieldModal}
                  disabled={!extraFieldGroupingData?.groupings?.length}
                  style={{ fontSize: 13, paddingLeft: 40, paddingRight: 40 }}>
                  + CREATE EXTRA FIELD
                </Button>
              </span>
            </Tooltip>
          )}
        </div>
      </div>
      <div className={s.content}>
        <Label>
          Sometimes you would want to capture more data then what ShowsHappening asks in checkout
          window.<br></br> Through this window, you will be able to define fields which you can add
          in the checkout window. Such extra fields can either be captured for each order or for
          each ticket.
        </Label>
        <Tabs
          defaultActiveKey="group"
          onChange={handleTabChange}
          tabBarStyle={{ color: '#8D949C' }}
          style={{ width: '100%', height: '100%' }}
          items={[
            {
              key: 'group',
              label: 'Create grouping',
              children: (
                <div className={s.notificationList}>
                  {fetching ? (
                    <div className={s.emptyNotificatoins}>
                      <Spin indicator={<EventLoadingState size={250} />} />
                    </div>
                  ) : !extraFieldGroupingData?.groupings ||
                    extraFieldGroupingData?.groupings?.length === 0 ? (
                    <div className={s.emptyNotificatoins}>
                      <div className={s.iconWrapper}>
                        <Icon name="listView" color="grey" />
                      </div>
                      <Typography color="grey">You have no groupings for this event yet</Typography>
                      <Button
                        type="primary"
                        size="large"
                        onClick={showModal}
                        style={{ fontSize: 13, paddingLeft: 40, paddingRight: 40 }}>
                        + CREATE GROUPING
                      </Button>
                    </div>
                  ) : (
                    <div style={{ overflowX: 'auto' }}>
                      <Row
                        gutter={[0, 8]}
                        style={{
                          minWidth: 600,
                          paddingLeft: 24,
                          paddingRight: 24,
                          paddingBottom: 8,
                          borderBottom: '1px solid #eff0f5',
                        }}>
                        <Col span={5}>
                          <Typography type="label">Name</Typography>
                        </Col>
                        <Col span={5}>
                          <Typography type="label">Show Title?</Typography>
                        </Col>
                        <Col span={5}>
                          <Typography type="label">Sort Order</Typography>
                        </Col>
                        <Col span={5}>
                          <Typography type="label">Capture type</Typography>
                        </Col>
                        <Col span={4}>
                          <Typography type="label">Actions</Typography>
                        </Col>
                      </Row>
                      {extraFieldGroupingData?.groupings &&
                        extraFieldGroupingData?.groupings.map((grouping, index) => (
                          <ExtraFieldGroupingItem
                            key={index}
                            id={grouping?.id}
                            setEditGrouping={handleEditItem}
                            handleDeleteItem={handleDeleteItem}
                            grouping={grouping}
                          />
                        ))}
                    </div>
                  )}
                </div>
              ),
            },
            {
              key: 'extraFields',
              label: 'Extra Fields',
              children: (
                <div className={s.notificationList}>
                  {fetching ? (
                    <div className={s.emptyNotificatoins}>
                      <Spin indicator={<EventLoadingState size={250} />} />
                    </div>
                  ) : !extraFieldGroupingData?.groupings?.some(
                      (grouping) => (grouping.extraFields || []).length > 0,
                    ) ? (
                    <div className={s.emptyNotificatoins}>
                      <div className={s.iconWrapper}>
                        <Icon name="file" color="grey" />
                      </div>
                      <Typography color="grey">
                        {!extraFieldGroupingData?.groupings?.length
                          ? 'At least one grouping is required to create an extra field.'
                          : 'You have no extra field for this event yet'}
                      </Typography>
                      <Tooltip
                        title={
                          !extraFieldGroupingData?.groupings?.length
                            ? 'Create a grouping first before adding extra fields.'
                            : ''
                        }>
                        <span>
                          <Button
                            type="primary"
                            size="large"
                            onClick={showFieldModal}
                            disabled={!extraFieldGroupingData?.groupings?.length}
                            style={{ fontSize: 13, paddingLeft: 40, paddingRight: 40 }}>
                            + CREATE EXTRA FIELD
                          </Button>
                        </span>
                      </Tooltip>
                    </div>
                  ) : (
                    <div style={{ overflowX: 'auto' }}>
                      <Row
                        gutter={[0, 8]}
                        style={{
                          minWidth: 600,
                          paddingLeft: 24,
                          paddingRight: 24,
                          paddingBottom: 8,
                          borderBottom: '1px solid #eff0f5',
                        }}>
                        <Col span={2}>
                          <Typography type="label">Field Type</Typography>
                        </Col>
                        <Col span={2}>
                          <Typography type="label">Caption</Typography>
                        </Col>
                        <Col span={2}>
                          <Typography type="label">Field Name</Typography>
                        </Col>
                        <Col span={4}>
                          <Typography type="label">Options Available</Typography>
                        </Col>
                        <Col span={2}>
                          <Typography type="label">Sort Order</Typography>
                        </Col>
                        <Col span={2}>
                          <Typography type="label">Grouping</Typography>
                        </Col>
                        <Col span={4}>
                          <Typography type="label">What to show on ticket (if any)</Typography>
                        </Col>
                        <Col span={2}>
                          <Typography type="label">Default Value</Typography>
                        </Col>
                        <Col span={2}>
                          <Typography type="label">Help Information</Typography>
                        </Col>
                        <Col span={2}>
                          <Typography type="label">Actions</Typography>
                        </Col>
                      </Row>
                      {extraFieldGroupingData.groupings.flatMap((grouping) =>
                        grouping?.extraFields?.map((field, fieldIndex) => (
                          <ExtraFieldsItem
                            key={fieldIndex}
                            id={field?.id ?? ''}
                            groupId={grouping?.id}
                            groupName={grouping?.name}
                            setExtraField={handleEditFieldItem}
                            handleDeleteFieldItem={handleDeleteFieldItem}
                            extraField={field}
                          />
                        )),
                      )}
                    </div>
                  )}
                </div>
              ),
            },
          ]}
        />
      </div>

      <Modal open={open} onOk={handleOk} handleClose={handleCancel} footer={null}>
        <Modal.Header>
          <Typography type="h3" weight={700}>
            {!editGrouping ? 'Create' : 'Edit'} grouping
          </Typography>
        </Modal.Header>
        <Form
          className={s.form}
          form={form}
          onFinish={handleSubmit}
          initialValues={{ sortOrder: 1 }}>
          <div>
            <Label>Name</Label>
            <Form.Item name="name" rules={[{ required: true, message: 'Name is required.' }]}>
              <Input name="name" placeholder="Type grouping name" />
            </Form.Item>
          </div>
          <div>
            <Typography type="main" weight={700} className={s.noMarginLable}>
              Show title?
            </Typography>
            <Form.Item
              name="showTitle"
              extra="If set to true a title will be shown in checkout process."
              initialValue={true} // or false, depending on default
            >
              <Switch />
            </Form.Item>
          </div>
          <div>
            <Label>Sort order</Label>
            <Form.Item
              fullWidth
              name="sortOrder"
              rules={[{ required: true, message: 'This field is required.' }]}>
              <Input.Number
                min={1}
                style={{ width: '100%' }}
                name="sortOrder"
                placeholder="Type sort order"
              />
            </Form.Item>
          </div>
          <div style={{ width: '100%' }}>
            <Label>Capture Type</Label>
            <Form.Item fullWidth name="captureType" initialValue="byOrder">
              <Select
                options={[
                  { label: 'By Order', value: 'byOrder' },
                  { label: 'By Ticket', value: 'byTicket' },
                ]}
              />
            </Form.Item>
          </div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginTop: 8,
              gap: 8,
              width: '100%',
            }}>
            <Button
              type="primary"
              size="large"
              loading={creating || editing}
              htmlType="submit"
              style={{ fontSize: 13, paddingLeft: 40, paddingRight: 40 }}>
              {!editGrouping ? 'CREATE' : 'EDIT'}
            </Button>
            <Button
              type="link"
              size="large"
              style={{ fontSize: 13, color: '#7F86A4' }}
              onClick={handleCancel}>
              CANCEL
            </Button>
          </div>
        </Form>
      </Modal>

      <Modal
        width={1000}
        open={openField}
        onOk={handleFieldOk}
        handleClose={handleFieldCancel}
        footer={null}>
        <Modal.Header>
          <Typography type="h3" weight={700}>
            {!editField ? 'Create' : 'Edit'} extra field
          </Typography>
        </Modal.Header>
        <Form
          className={s.form}
          form={fieldForm}
          onFinish={handleFieldSubmit}
          initialValues={{ sortOrder: 1 }}>
          <div>
            <Label>Field Type</Label>
            <Form.Item fullWidth name="fieldType" initialValue="string">
              <Select
                options={[
                  { label: 'String', value: 'string' },
                  { label: 'Number', value: 'number' },
                  { label: 'DropDown', value: 'dropdown' },
                  { label: 'TextArea', value: 'textArea' },
                  { label: 'CheckBox', value: 'checkBox' },
                  { label: 'File Upload', value: 'fileUpload' },
                ]}
              />
            </Form.Item>
          </div>
          <Row gutter={[8, 0]}>
            <Col span={12}>
              <Label>Caption</Label>
              <Form.Item name="caption">
                <Input name="caption" placeholder="Type field caption" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Label>Field Name</Label>
              <Form.Item
                name="name"
                rules={[
                  {
                    pattern: /^[a-zA-Z0-9]+$/,
                    message: 'Only letters and numbers are allowed',
                  },
                ]}>
                <Input name="name" placeholder="Type field name" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={[8, 8]}>
            {fieldType === 'dropdown' && (
              <Col span={24}>
                <Label>Options Available (if dropdownlist)</Label>
                <Form.Item name="optionsAvailable">
                  <TextArea name="optionsAvailable" />
                </Form.Item>
              </Col>
            )}

            <Col span={24}>
              <Label>Sort order</Label>
              <Form.Item fullWidth name="sortOrder">
                <Input.Number
                  min={1}
                  style={{ width: '100%' }}
                  name="sortOrder"
                  placeholder="Type sort order"
                />
              </Form.Item>
            </Col>
          </Row>
          <div style={{ width: '100%' }}>
            <Label required>Grouping</Label>
            <Form.Item
              fullWidth
              name="groupId"
              rules={[{ required: true, message: 'This field is required.' }]}>
              <Select
                options={
                  extraFieldGroupingData?.groupings?.map((grouping) => ({
                    label: grouping.name,
                    value: grouping.id,
                  })) || []
                }
              />
            </Form.Item>
          </div>

          <div>
            <Label>
              Text to show on ticket (leave empty if you do not wish that this is not printedon
              ticket)
            </Label>
            <Form.Item name="captionToShowOnTicket">
              <Input name="captionToShowOnTicket" placeholder="" />
            </Form.Item>
          </div>
          <div>
            <Label>Defaut Value</Label>
            <Form.Item name="defaultValue">
              <Input name="defaultValue" placeholder="" />
            </Form.Item>
          </div>
          <div>
            <Label>
              Help Information (Help information which will show near field in checkout window, to
              help user)
            </Label>
            <Form.Item name="helpInformation">
              <Input name="helpInformation" placeholder="" />
            </Form.Item>
          </div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginTop: 8,
              gap: 8,
              width: '100%',
            }}>
            <Button
              type="primary"
              size="large"
              loading={creatingExtraField || editingExtraField}
              htmlType="submit"
              style={{ fontSize: 13, paddingLeft: 40, paddingRight: 40 }}>
              {!editField ? 'CREATE' : 'UPDATE'}
            </Button>
            <Button
              type="link"
              size="large"
              style={{ fontSize: 13, color: '#7F86A4' }}
              onClick={handleFieldCancel}>
              CANCEL
            </Button>
          </div>
        </Form>
      </Modal>
    </div>
  );
};

export default Notifications;
